/// <amd-module name="Artifacts/Connect/tools/Npm" />
import { CopyableCode, DetailsCard, PublishDetailsCard } from "Artifacts/Connect/components/Common";
import { OperatingSystem } from "Artifacts/Connect/Constants";
import * as Res from "Artifacts/Connect/Resources";
import { PackagingService, TfsService } from "Artifacts/Connect/services/PackagingService";
import { ToolService } from "Artifacts/Connect/services/ToolService";
import { IToolConnectProps } from "Artifacts/Connect/tools/IToolConnectProps";
import { ICombinedFeedView } from "Artifacts/Connect/types/ICombinedFeedView";
import { IProtocol } from "Artifacts/Connect/types/IProtocol";
import { ITool, ToolId } from "Artifacts/Connect/types/ITool";
import { FWLink } from "Artifacts/Root/components/FWLink";
import { getCurrentCollectionName, getIsHosted } from "Artifacts/Root/helpers/CollectionHelpers";
import { getIsUserMember } from "Artifacts/Root/helpers/UserHelpers";
import * as React from "react";
import { useMemo } from "react";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssPageContext } from "VSS/Platform/Context";
import { IPageData } from "VSS/Platform/Page";
import * as Utils_Url from "VSS/Platform/Util/Url";
import { replaceRouteValues } from "VSS/Platform/Util/Url";
import { Link } from "VSSUI/Link";
import { Tab, TabBar, TabSize } from "VSSUI/Tabs";

const NpmConnectComponent: React.FC<IToolConnectProps<NpmProtocol>> = ({
  pageContext,
  protocol,
  feed,
  packagingService,
  tfsService,
}) => {
  const [selectedTabId, setSelectedTabId] = React.useState<string>(OperatingSystem.Windows);
  const registry = protocol.getFeedUrl(packagingService, feed);
  const nerfDart = protocol.getNerfDart(packagingService, feed);

  const stripRegistry = true;
  const nerfDartShort = protocol.getNerfDart(packagingService, feed, stripRegistry);

  const tfsServiceUrl = tfsService.getSessionTokenUri();

  const { collectionName, userIsMember, isHosted } = useMemo(() => {
    return {
      collectionName: getCurrentCollectionName(pageContext),
      userIsMember: getIsUserMember(pageContext),
      isHosted: getIsHosted(pageContext),
    };
  }, [pageContext]);

  if (userIsMember) {
    return (
      <React.Fragment>
        <DetailsCard title={{ text: Res.ProjectSetup, ariaLevel: 3 }}>
          <TabBar
            onSelectedTabChanged={setSelectedTabId}
            selectedTabId={selectedTabId}
            tabSize={TabSize.Compact}
          >
            <Tab
              name="Windows"
              id={OperatingSystem.Windows}
              iconProps={{ iconName: "WindowsLogo" }}
            />
            <Tab name={Res.Other} id={OperatingSystem.Other} iconProps={{ iconName: "PC1" }} />
          </TabBar>

          <p className="tooldetails-smaller-margin">{Res.ProjectSetup_Npm_Details}</p>
          <CopyableCode ariaLabel={Res.ProjectSetup}>
            <pre>{`registry=${registry} 
                        
always-auth=true`}</pre>
          </CopyableCode>

          {selectedTabId === OperatingSystem.Windows && isHosted && (
            <React.Fragment>
              <p>{Res.ProjectSetup_Npm_Windows_Details}</p>
              <CopyableCode ariaLabel={Res.ProjectSetup}>
                {Res.NpmConnectControl_AuthHelperCommand}
              </CopyableCode>
              <p className="body-s">{Res.ProjectSetup_Npm_Windows_Note}</p>
            </React.Fragment>
          )}

          {selectedTabId === OperatingSystem.Windows && (
            <React.Fragment>
              <p className="body-s">
                <FormatComponent format={Res.PAT_Issues}>
                  <Link
                    underline
                    className="body-s"
                    onClick={() => setSelectedTabId(OperatingSystem.Other)}
                  >
                    {Res.PAT_Instructions}
                  </Link>
                </FormatComponent>
              </p>
            </React.Fragment>
          )}

          {selectedTabId === OperatingSystem.Other && (
            <React.Fragment>
              <h4 className="title-s margin-top-16">{Res.SetupCredentials}</h4>
              <div className="rhythm-vertical-20 card-steps margin-top-8">
                <div>
                  <h5 className="title-xs">{Res.Step} 1</h5>
                  <p>
                    <FormatComponent format={Res.Npm_CopyToNpmrc}>
                      <FWLink underline linkId="724423">
                        {Res.UserNpmrc}
                      </FWLink>
                    </FormatComponent>
                  </p>
                  <CopyableCode ariaLabel={Res.ProjectSetup}>
                    ; {Res.BeginAuthToken} <br />
                    {nerfDart}:username={collectionName} <br />
                    {nerfDart}:_password=[{Res.EncodedPAT}] <br />
                    {nerfDart}:email={Res.NpmRequiresEmail}
                    <br />
                    {nerfDartShort}:username={collectionName} <br />
                    {nerfDartShort}:_password=[{Res.EncodedPAT}] <br />
                    {nerfDartShort}:email={Res.NpmRequiresEmail}
                    <br />; {Res.EndAuthToken}
                  </CopyableCode>
                </div>

                <div>
                  <h5 className="title-xs">{Res.Step} 2</h5>
                  <p>
                    <FormatComponent format={Res.GeneratePAT}>
                      <Link underline href={tfsServiceUrl} target="_blank">
                        {Res.PersonalAccessToken}
                      </Link>
                      <em>{Res.Packaging}</em>
                    </FormatComponent>
                  </p>
                </div>

                <div>
                  <h5 className="title-xs">{Res.Step} 3</h5>
                  <p>{Res.Npm_BaseEncodePAT}</p>
                  <p>{Res.Npm_HowToEncode_Preamble}</p>
                  <ol type="1">
                    <li>1. {Res.Npm_RunCommand}</li>
                    <li className="base64-encode-instructions">
                      <CopyableCode ariaLabel={Res.ProjectSetup}>
                        {`node -e "require('readline') .createInterface({input:process.stdin,output:process.stdout,historySize:0}) .question('PAT> ',p => { b64=Buffer.from(p.trim()).toString('base64');console.log(b64);process.exit(); })"`}
                      </CopyableCode>
                    </li>
                    <li>2. {Res.Npm_PastePAT}</li>
                    <li>3. {Res.Npm_CopyEncodedValue}</li>
                  </ol>
                </div>

                <div>
                  <h5 className="title-xs">{Res.Step} 4</h5>
                  <p>
                    <FormatComponent format={Res.Npm_UpdateUserNpmrc}>
                      <em>{Res.Both}</em>
                      <code>[{Res.EncodedPAT}]</code>
                      <code>.npmrc</code>
                      <em>{Res.Base64}</em>
                    </FormatComponent>
                  </p>
                </div>
              </div>
            </React.Fragment>
          )}
        </DetailsCard>

        <DetailsCard title={{ text: Res.RestorePackages, ariaLevel: 3 }}>
          <p>{Res.RunCommand_Directory}</p>
          <CopyableCode ariaLabel={Res.RestorePackages}>npm install</CopyableCode>
        </DetailsCard>

        <PublishDetailsCard feed={feed}>
          <p>{Res.RunCommand_Directory}</p>
          <CopyableCode ariaLabel={Res.PublishPackages}>npm publish</CopyableCode>
        </PublishDetailsCard>
      </React.Fragment>
    );
  } else {
    return (
      <React.Fragment>
        <DetailsCard title={{ text: Res.ProjectSetup, ariaLevel: 3 }}>
          <p>
            <FormatComponent format={Res.Npm_ScopedNpmrc}>
              <code>.npmrc</code>
              <code>package.json</code>
            </FormatComponent>
          </p>
          <CopyableCode ariaLabel={Res.ProjectSetup}>
            @[YOUR_SCOPE]/registry={registry}
          </CopyableCode>
        </DetailsCard>

        <DetailsCard title={{ text: Res.RestorePackages, ariaLevel: 3 }}>
          <p>{Res.RunCommand_Directory}</p>
          <CopyableCode ariaLabel={Res.RestorePackages}>npm install</CopyableCode>
        </DetailsCard>
      </React.Fragment>
    );
  }
};

export class NpmProtocol implements IProtocol {
  readonly name = "npm";
  readonly area = "npm";

  getFeedUrl(packagingService: PackagingService, feed: ICombinedFeedView) {
    // TODO: Move route resolution to endpoints within this object
    const path = replaceRouteValues("_packaging/{feedId}/{area}/{resource}{packageName}", {
      feedId: feed.name,
      area: this.area,
      resource: "registry",
    });

    return packagingService.getUrl(feed.project, path + "/");
  }

  getNerfDart(
    packagingService: PackagingService,
    feed: ICombinedFeedView,
    stripRegistry = false,
  ): string {
    const feedUrl = this.getFeedUrl(packagingService, feed);
    const urlWithRegistry = this.getNpmAuthUrl(feedUrl);

    if (stripRegistry) {
      return urlWithRegistry.replace(/\/registry\/?$/, "/");
    }

    return urlWithRegistry;
  }

  getNpmAuthUrl(registryEndpointUrl: string): string {
    // Old code implementation. Reasoning for doing this is unclear.
    // TODO: Further investigation required before modifying.
    const uri = new Utils_Url.Uri(registryEndpointUrl);

    // @ts-ignore TypeScript 4.0 upgrade
    delete uri.scheme;
    // @ts-ignore TypeScript 4.0 upgrade
    delete uri.queryParameters;
    // @ts-ignore TypeScript 4.0 upgrade
    delete uri.hashString;

    const lastSlashIndex = uri.path.lastIndexOf("/");
    // include everything up to the last slash, discard everything after it (including the slash):
    const path = lastSlashIndex > 0 ? "/" + uri.path.substr(0, lastSlashIndex) : "";

    // absoluteUri encodes the path, which means it won't match the registry uri property. We'll append it ourselves.
    // @ts-ignore TypeScript 4.0 upgrade
    delete uri.path;

    // this is mimicking NPM's 'toNerfDart' behavior.
    return "//" + Utils_Url.combineUrlPaths(uri.absoluteUri, path) + "/";
  }
}

export class NpmTool implements ITool {
  readonly id = ToolId.npm;
  readonly name = "npm";
  readonly protocol = new NpmProtocol();
  readonly allowNonMembers = true;
  readonly imagePath = `ms.azure-artifacts/connect/img/npm.svg`;
  readonly Component: React.ComponentType = NpmConnectComponent;
  readonly toolLinkId = 2103788;

  public getToolLink = () => {
    return {
      href: `https://go.microsoft.com/fwlink/?linkid=${this.toolLinkId}`,
      children: `${Res.CommandLineReference}`,
    };
  };

  public isProtocolEnabled(pageContext: IVssPageContext): boolean {
    return true;
  }

  isEnvironmentSupported(pageData: IPageData): boolean {
    return true;
  }

  supportsGet(): boolean {
    return true;
  }

  supportsPush(feed: ICombinedFeedView): boolean {
    return feed.canPublishPackages;
  }

  renderComponent(
    pageContext: IVssPageContext,
    feed: ICombinedFeedView,
    packagingService: PackagingService,
    tfsService: TfsService,
  ): JSX.Element {
    return (
      <NpmConnectComponent
        pageContext={pageContext}
        protocol={this.protocol}
        feed={feed}
        packagingService={packagingService}
        tfsService={tfsService}
      />
    );
  }
}

ToolService.registerTool(new NpmTool());
